import type { ComponentType } from 'react';
import { Navigate, Outlet, useLocation, type RouteObject } from 'react-router';
import type { FeatureKey } from '@growhero/shared';
import { AuthLayout } from './layouts/AuthLayout';
import { PublicLayout } from './layouts/CenteredLayout';
import { AdminShell, AppShell } from './shell/AppShell';
import {
  RedirectIfAuthed,
  RequireActiveSubscription,
  RequireAuth,
  RequirePlatformAdmin,
  RequireTenant,
} from './guards';
import { FullPageLoader } from './FullPage';
import { FeatureGate, PlatformFeatureGate } from './ProLock';
import { NotFoundPage, RouteErrorPage } from './SystemPages';

/**
 * ALL routes of the web app (shared registry — feature agents edit only their own lines).
 * Pages are lazy-loaded; each lives in src/features/<feature>/<Name>Page.tsx with a default export.
 * Pro-only sections are wrapped in <Gate feature=…> so Basic tenants see the ProLock upsell.
 */
type PageModule = { default: ComponentType };
/** Marks catch-all routes, so guards can tell an unknown URL from a protected one. */
const NOT_FOUND = { notFound: true } as const;
const page = (load: () => Promise<PageModule>) => async () => ({
  Component: (await load()).default,
});

function Gate({ feature }: { feature: FeatureKey }) {
  return (
    <FeatureGate feature={feature}>
      <Outlet />
    </FeatureGate>
  );
}

/** A platform-switched feature that is off redirects to `to` (no page, no upsell). */
function PlatformGate({ feature, to }: { feature: FeatureKey; to: string }) {
  return (
    <PlatformFeatureGate feature={feature} to={to}>
      <Outlet />
    </PlatformFeatureGate>
  );
}

/** Backend fake-payments mode links to /fake-checkout?session=… — keep the query, use the canonical path. */
function FakeCheckoutAlias() {
  const { search } = useLocation();
  return <Navigate to={`/billing/fake-checkout${search}`} replace />;
}

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    // Every page is lazy: on a full page load the router renders this until the first match's
    // module is in (and React Router stops warning "No HydrateFallback element provided").
    hydrateFallbackElement: <FullPageLoader />,
    children: [
      // ── Public: auth ──────────────────────────────────────────────
      {
        element: (
          <RedirectIfAuthed>
            <AuthLayout />
          </RedirectIfAuthed>
        ),
        children: [
          { path: 'login', lazy: page(() => import('@/features/auth/LoginPage')) },
          { path: 'register', lazy: page(() => import('@/features/auth/RegisterPage')) },
          { path: 'reset-password', lazy: page(() => import('@/features/auth/ResetPasswordPage')) },
        ],
      },

      // ── Public: customer-facing token pages ──────────────────────
      {
        element: <PublicLayout />,
        children: [
          { path: 'r/:token', lazy: page(() => import('@/features/public/ReviewLandingPage')) },
          { path: 'u/:token', lazy: page(() => import('@/features/public/UnsubscribePage')) },
          // Onboarding test-message preview (onboarding module): demo landing, nothing recorded.
          { path: 'p/:token', lazy: page(() => import('@/features/onboarding/ReviewPreviewPage')) },
          {
            path: 'billing/fake-checkout',
            lazy: page(() => import('@/features/billing/FakeCheckoutPage')),
          },
        ],
      },
      { path: 'fake-checkout', element: <FakeCheckoutAlias /> },

      // ── Public: form pages (owner-themed, full bleed, no app chrome) ──
      {
        path: 'f/preview/:token',
        lazy: page(() => import('@/features/public/form/PreviewFormPage')),
      },
      { path: 'f/:token', lazy: page(() => import('@/features/public/form/PersonalFormPage')) },
      { path: 's/:code', lazy: page(() => import('@/features/public/form/SharedFormPage')) },

      // ── Signed in ─────────────────────────────────────────────────
      {
        element: <RequireAuth />,
        children: [
          // Setup: create a business (no tenant required yet)
          { path: 'setup', lazy: page(() => import('@/features/setup/SetupBusinessPage')) },
          // Accept an invitation to an existing business (no tenant required yet)
          { path: 'invite/:token', lazy: page(() => import('@/features/setup/AcceptInvitePage')) },

          {
            element: <RequireTenant />,
            children: [
              { path: 'setup/plan', lazy: page(() => import('@/features/billing/PlanSelectPage')) },
              {
                path: 'setup/payment-pending',
                lazy: page(() => import('@/features/billing/PaymentPendingPage')),
              },

              {
                element: <RequireActiveSubscription />,
                children: [
                  {
                    path: 'onboarding',
                    lazy: page(() => import('@/features/setup/OnboardingPage')),
                  },

                  // ── Tenant portal ──────────────────────────────────
                  {
                    element: <AppShell />,
                    children: [
                      { index: true, lazy: page(() => import('@/features/home/HomePage')) },
                      {
                        element: <Gate feature="dashboard.codegen" />,
                        children: [
                          {
                            path: 'home/studio',
                            lazy: page(() => import('@/features/home/ai-dashboard/StudioPage')),
                          },
                        ],
                      },

                      {
                        path: 'contacts',
                        lazy: page(() => import('@/features/contacts/ContactsListPage')),
                      },
                      {
                        path: 'contacts/import',
                        lazy: page(() => import('@/features/contacts/ContactsImportPage')),
                      },
                      {
                        path: 'contacts/:id',
                        lazy: page(() => import('@/features/contacts/ContactDetailPage')),
                      },

                      {
                        path: 'reviews',
                        lazy: page(() => import('@/features/reviews/ReviewsOverviewPage')),
                      },
                      {
                        path: 'reviews/requests',
                        lazy: page(() => import('@/features/reviews/ReviewRequestsPage')),
                      },
                      {
                        path: 'reviews/feedback',
                        lazy: page(() => import('@/features/reviews/ReviewFeedbackPage')),
                      },
                      {
                        path: 'reviews/settings',
                        lazy: page(() => import('@/features/reviews/ReviewSettingsPage')),
                      },
                      {
                        // Google Business Profile: hidden unless the API turns it on (ADR-G7).
                        element: <PlatformGate feature="google.business" to="/reviews" />,
                        children: [
                          {
                            element: <Gate feature="google.business" />,
                            children: [
                              {
                                path: 'reviews/google',
                                lazy: page(() => import('@/features/reviews/GoogleReviewsPage')),
                              },
                            ],
                          },
                        ],
                      },

                      {
                        path: 'messages',
                        lazy: page(() => import('@/features/messages/MessagesListPage')),
                      },
                      {
                        path: 'messages/new',
                        lazy: page(() => import('@/features/messages/MessageComposePage')),
                      },
                      {
                        path: 'messages/templates',
                        lazy: page(() => import('@/features/messages/email/EmailTemplatesPage')),
                      },
                      {
                        path: 'messages/templates/:id',
                        lazy: page(
                          () => import('@/features/messages/email/EmailTemplateStudioPage'),
                        ),
                      },
                      {
                        path: 'messages/:id',
                        lazy: page(() => import('@/features/messages/MessageDetailPage')),
                      },

                      {
                        element: <Gate feature="forms.interest" />,
                        children: [
                          {
                            path: 'interest',
                            lazy: page(() => import('@/features/forms/InterestListPage')),
                          },
                          {
                            path: 'interest/new',
                            lazy: page(() => import('@/features/forms/InterestNewPage')),
                          },
                          {
                            path: 'interest/:id',
                            lazy: page(() => import('@/features/forms/InterestDetailPage')),
                          },
                          {
                            path: 'interest/:id/edit',
                            lazy: page(() => import('@/features/forms/InterestWizardPage')),
                          },
                        ],
                      },
                      {
                        element: <Gate feature="forms.events" />,
                        children: [
                          {
                            path: 'events',
                            lazy: page(() => import('@/features/forms/EventsListPage')),
                          },
                          {
                            path: 'events/new',
                            lazy: page(() => import('@/features/forms/EventsNewPage')),
                          },
                          {
                            path: 'events/:id',
                            lazy: page(() => import('@/features/forms/EventDetailPage')),
                          },
                          {
                            path: 'events/:id/edit',
                            lazy: page(() => import('@/features/forms/EventWizardPage')),
                          },
                          {
                            path: 'events/:id/check-in',
                            lazy: page(() => import('@/features/forms/EventCheckInPage')),
                          },
                        ],
                      },

                      {
                        element: <Gate feature="segments" />,
                        children: [
                          {
                            path: 'segments',
                            lazy: page(() => import('@/features/segments/SegmentsPage')),
                          },
                          {
                            path: 'segments/new',
                            lazy: page(() => import('@/features/segments/SegmentBuilderPage')),
                          },
                          {
                            path: 'segments/:id',
                            lazy: page(() => import('@/features/segments/SegmentBuilderPage')),
                          },
                        ],
                      },
                      {
                        element: <Gate feature="automations" />,
                        children: [
                          {
                            path: 'automations',
                            lazy: page(() => import('@/features/automations/AutomationsPage')),
                          },
                          {
                            path: 'automations/new/:templateKey',
                            lazy: page(() => import('@/features/automations/WorkflowWizardPage')),
                          },
                          {
                            path: 'automations/:id',
                            lazy: page(() => import('@/features/automations/WorkflowDetailPage')),
                          },
                          {
                            path: 'automations/:id/edit',
                            lazy: page(() => import('@/features/automations/WorkflowWizardPage')),
                          },
                        ],
                      },
                      {
                        element: <Gate feature="insights" />,
                        children: [
                          {
                            path: 'insights',
                            lazy: page(() => import('@/features/insights/InsightsPage')),
                          },
                        ],
                      },
                      {
                        element: <Gate feature="ai.assistant" />,
                        children: [
                          {
                            path: 'assistant',
                            lazy: page(() => import('@/features/assistant/AssistantPage')),
                          },
                          {
                            path: 'assistant/memory',
                            lazy: page(() => import('@/features/assistant/AssistantMemoryPage')),
                          },
                        ],
                      },
                      {
                        element: <Gate feature="schema.custom" />,
                        children: [
                          {
                            path: 'data',
                            lazy: page(() => import('@/features/data/DataSchemaPage')),
                          },
                          {
                            path: 'data/objects/:key',
                            lazy: page(() => import('@/features/data/ObjectRecordsPage')),
                          },
                          {
                            path: 'data/objects/:key/:id',
                            lazy: page(() => import('@/features/data/ObjectRecordPage')),
                          },
                        ],
                      },

                      {
                        path: 'billing',
                        lazy: page(() => import('@/features/billing/BillingPage')),
                      },

                      {
                        path: 'settings',
                        lazy: page(() => import('@/features/settings/SettingsPage')),
                      },
                      {
                        path: 'settings/team',
                        lazy: page(() => import('@/features/settings/TeamSettingsPage')),
                      },
                      {
                        path: 'settings/store',
                        lazy: page(() => import('@/features/settings/StoreSettingsPage')),
                      },

                      { path: '*', element: <NotFoundPage />, handle: NOT_FOUND },
                    ],
                  },
                ],
              },
            ],
          },

          // ── Super Admin ───────────────────────────────────────────────
          {
            path: 'platform-admin',
            element: (
              <RequirePlatformAdmin>
                <AdminShell />
              </RequirePlatformAdmin>
            ),
            children: [
              { index: true, lazy: page(() => import('@/features/admin/AdminOverviewPage')) },
              { path: 'tenants', lazy: page(() => import('@/features/admin/AdminTenantsPage')) },
              {
                path: 'tenants/:id',
                lazy: page(() => import('@/features/admin/AdminTenantDetailPage')),
              },
              { path: 'plans', lazy: page(() => import('@/features/admin/AdminPlansPage')) },
              { path: 'models', lazy: page(() => import('@/features/admin/AdminModelsPage')) },
              {
                path: 'providers',
                lazy: page(() => import('@/features/admin/AdminProvidersPage')),
              },
              { path: 'audit', lazy: page(() => import('@/features/admin/AdminAuditPage')) },
              { path: '*', element: <NotFoundPage />, handle: NOT_FOUND },
            ],
          },
        ],
      },

      // ── Dev-only component kitchen sink ──────────────────────────
      ...(import.meta.env.DEV
        ? [{ path: 'dev/ui', lazy: page(() => import('@/dev/KitchenSink')) }]
        : []),

      { path: '*', element: <NotFoundPage />, handle: NOT_FOUND },
    ],
  },
];
